export const WORKLYNX_SESSION_PREFIX = 'worklynx:';
export const DEFAULT_SESSION_PERSISTENCE_TTL_MS = 12 * 60 * 60 * 1000;

const ENVELOPE_KIND = 'worklynx.session.v1';

interface PersistedSessionEnvelope<T> {
  kind: typeof ENVELOPE_KIND;
  version: number;
  savedAt: number;
  expiresAt: number;
  value: T;
}

interface SessionPersistenceOptions<T> {
  ttlMs?: number;
  version?: number;
  validate?: (value: unknown) => T | null;
}

export function readSessionPersistedValue<T>(
  storageKey: string,
  options: SessionPersistenceOptions<T> = {},
): T | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as unknown;
    const envelope = toPersistedEnvelope<T>(parsed);
    const value = envelope ? readEnvelope(storageKey, envelope, options) : parsed;
    if (value == null) return null;

    return options.validate ? options.validate(value) : (value as T);
  } catch {
    return null;
  }
}

export function writeSessionPersistedValue<T>(
  storageKey: string,
  value: T,
  options: Pick<SessionPersistenceOptions<T>, 'ttlMs' | 'version'> = {},
) {
  if (typeof window === 'undefined') return;

  try {
    const savedAt = Date.now();
    const ttlMs = options.ttlMs ?? DEFAULT_SESSION_PERSISTENCE_TTL_MS;
    const envelope: PersistedSessionEnvelope<T> = {
      kind: ENVELOPE_KIND,
      version: options.version ?? 1,
      savedAt,
      expiresAt: savedAt + ttlMs,
      value,
    };

    window.sessionStorage.setItem(storageKey, JSON.stringify(envelope));
  } catch {
    // Ignore browsers that block storage access.
  }
}

export function clearSessionPersistedValue(storageKey: string) {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.removeItem(storageKey);
  } catch {
    // Ignore browsers that block storage access.
  }
}

export function clearWorklynxSessionPersistence() {
  if (typeof window === 'undefined') return;

  try {
    for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(WORKLYNX_SESSION_PREFIX)) {
        window.sessionStorage.removeItem(key);
      }
    }
  } catch {
    // Ignore browsers that block storage access.
  }
}

function readEnvelope<T>(
  storageKey: string,
  envelope: PersistedSessionEnvelope<T>,
  options: SessionPersistenceOptions<T>,
): T | null {
  if (Number.isFinite(envelope.expiresAt) && envelope.expiresAt <= Date.now()) {
    clearSessionPersistedValue(storageKey);
    return null;
  }

  if (options.version != null && envelope.version !== options.version) {
    clearSessionPersistedValue(storageKey);
    return null;
  }

  return envelope.value;
}

function toPersistedEnvelope<T>(value: unknown): PersistedSessionEnvelope<T> | null {
  if (!value || typeof value !== 'object') return null;

  const candidate = value as Partial<PersistedSessionEnvelope<T>>;
  if (
    candidate.kind !== ENVELOPE_KIND ||
    typeof candidate.savedAt !== 'number' ||
    !Number.isFinite(candidate.savedAt) ||
    typeof candidate.expiresAt !== 'number' ||
    !Number.isFinite(candidate.expiresAt) ||
    !('value' in candidate)
  ) {
    return null;
  }

  return candidate as PersistedSessionEnvelope<T>;
}
